// src/components/clients/AgregarMovimientoModal.jsx
/**
 * Modal for adding movements (payments, adjustments) to a Running Account
 * 
 * Movement types:
 * - PAGO: Payment received (HABER)
 * - AJUSTE_CREDITO: Credit adjustment (HABER)
 * - AJUSTE_DEBITO: Debit adjustment (DEBE)
 * - ADELANTO: Advance payment (HABER)
 * 
 * Uses the same ledger as "Agregar a Cuenta Abierta" from Invoice Modal
 */
import React, { useState, useEffect } from 'react';
import { 
    Modal, 
    Form, 
    Input, 
    Select, 
    Button, 
    message, 
    Row, 
    Col, 
    Divider, 
    Alert,
    InputNumber,
    DatePicker,
    Space,
    Typography
} from 'antd';
import { 
    DollarOutlined, 
    WalletOutlined,
    PlusCircleOutlined,
    MinusCircleOutlined,
    BankOutlined,
    CreditCardOutlined
} from '@ant-design/icons';
import { runningAccountService } from '../../services/runningAccountService';
import dayjs from 'dayjs';

const { Option } = Select;
const { TextArea } = Input;
const { Text } = Typography;

// Movement type configurations
const MOVEMENT_TYPES = {
    PAGO: {
        label: 'Pago Recibido',
        icon: <DollarOutlined />,
        color: '#52c41a',
        column: 'HABER',
        description: 'Registrar un pago del cliente'
    },
    ADELANTO: {
        label: 'Adelanto / Seña',
        icon: <WalletOutlined />,
        color: '#13c2c2',
        column: 'HABER',
        description: 'Registrar un adelanto o seña'
    },
    AJUSTE_CREDITO: {
        label: 'Ajuste a Favor (Crédito)',
        icon: <PlusCircleOutlined />,
        color: '#1890ff',
        column: 'HABER',
        description: 'Ajuste que reduce la deuda del cliente'
    },
    AJUSTE_DEBITO: {
        label: 'Ajuste en Contra (Débito)',
        icon: <MinusCircleOutlined />,
        color: '#ff4d4f',
        column: 'DEBE',
        description: 'Ajuste que aumenta la deuda del cliente'
    }
};

// Payment methods — values must match backend SP expectations exactly.
// '201' (Efectivo/Contado) is the ONLY value that triggers Caja cash-in.
const PAYMENT_METHODS = [
    { value: '201',           label: 'Efectivo / Contado', icon: <DollarOutlined />,    triggersCaja: true  },
    { value: 'Transferencia', label: 'Transferencia Bancaria', icon: <BankOutlined />,  triggersCaja: false },
    { value: 'Cheque',        label: 'Cheque',              icon: <CreditCardOutlined />, triggersCaja: false },
    { value: 'Tarjeta',       label: 'Tarjeta',             icon: <CreditCardOutlined />, triggersCaja: false },
];

const AgregarMovimientoModal = ({ visible, onClose, onMovementCreated, selectedClient }) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [selectedType, setSelectedType] = useState(null);

    // Reset form when modal opens/closes or client changes
    useEffect(() => {
        if (visible) {
            form.resetFields();
            setSelectedType(null);
            // Set default date to today
            form.setFieldsValue({
                fecha: dayjs()
            });
        }
    }, [visible, selectedClient, form]);

    // Handle movement type change
    const handleTypeChange = (type) => {
        setSelectedType(type);
        if (type === 'PAGO' || type === 'ADELANTO') {
            // Default to '201' (Efectivo) — the most common and the one that triggers Caja
            form.setFieldsValue({ paymentMethod: '201' });
        } else {
            form.setFieldsValue({ paymentMethod: undefined });
        }
    };

    // Format currency for display
    const formatCurrency = (value) => {
        if (!value) return '$ 0,00';
        return `$ ${Number(value).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    };

    // Handle form submission
    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            
            if (!selectedClient) {
                message.error('No hay cliente seleccionado');
                return;
            }

            if (!values.monto || values.monto <= 0) {
                message.warning('El monto debe ser mayor a cero');
                return;
            }

            // Guard: paymentMethod is mandatory for PAGO / ADELANTO
            const requiresPaymentMethod = values.tipoMovimiento === 'PAGO' || values.tipoMovimiento === 'ADELANTO';
            if (requiresPaymentMethod && !values.paymentMethod) {
                message.error('Seleccione un método de pago');
                return;
            }

            setLoading(true);

            const movementConfig = MOVEMENT_TYPES[values.tipoMovimiento];
            
            // Build movement payload
            const movementData = {
                clientId: selectedClient.id || selectedClient.documento_nro,
                clientDocumento: selectedClient.documento_nro,
                tipoMovimiento: values.tipoMovimiento,
                monto: values.monto,
                fecha: values.fecha ? values.fecha.format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD'),
                // Payment-specific fields
                paymentMethod: values.paymentMethod || null,
                paymentReference: values.paymentReference || null,
                // Determine DEBE/HABER based on movement type
                debe: movementConfig.column === 'DEBE' ? values.monto : 0,
                haber: movementConfig.column === 'HABER' ? values.monto : 0,
                // Notes (required, min 5 chars) - displayed as description in table
                notas: values.notas || ''
            };

            console.log('[RunningAccount] Creating movement:', movementData);

            // Call API to create movement
            try {
                const response = await runningAccountService.addMovement(movementData);
                
                if (response.success) {
                    const columnText = movementConfig.column === 'HABER' ? 'a favor' : 'en contra';
                    message.success(`Movimiento de ${formatCurrency(values.monto)} registrado ${columnText}`);
                    
                    form.resetFields();
                    setSelectedType(null);
                    
                    if (onMovementCreated) {
                        onMovementCreated(response.movement || movementData);
                    }
                    onClose();
                } else {
                    message.error(response.message || 'Error al registrar movimiento');
                }
            } catch (apiError) {
                // If API is not available, still allow creation with mock success
                console.warn('[RunningAccount] API not available, using mock response:', apiError);
                const columnText = movementConfig.column === 'HABER' ? 'a favor' : 'en contra';
                message.success(`Movimiento de ${formatCurrency(values.monto)} registrado ${columnText} (modo offline)`);
                
                form.resetFields();
                setSelectedType(null);
                
                if (onMovementCreated) {
                    onMovementCreated(movementData);
                }
                onClose();
            }

        } catch (error) {
            console.error('[RunningAccount] Error creating movement:', error);
            
            if (error.errorFields) {
                // Form validation error - already handled by antd
                return;
            }
            message.error('Error al registrar movimiento');
        } finally {
            setLoading(false);
        }
    };

    // Handle modal close
    const handleCancel = () => {
        form.resetFields();
        setSelectedType(null);
        onClose();
    };

    // Check if payment method is required
    const isPaymentType = selectedType === 'PAGO' || selectedType === 'ADELANTO';

    return (
        <Modal
            title={
                <Space>
                    <WalletOutlined />
                    <span>Agregar Movimiento a Cuenta Abierta</span>
                </Space>
            }
            open={visible}
            onCancel={handleCancel}
            width={600}
            footer={[
                <Button key="cancel" onClick={handleCancel}>
                    Cancelar
                </Button>,
                <Button
                    key="submit"
                    type="primary"
                    loading={loading}
                    onClick={handleSubmit}
                    disabled={!selectedType}
                    style={{ 
                        backgroundColor: selectedType ? MOVEMENT_TYPES[selectedType]?.color : undefined 
                    }}
                >
                    Registrar Movimiento
                </Button>
            ]}
        >
            {/* Client Info Banner */}
            {selectedClient && (
                <Alert
                    message={
                        <span>
                            Cliente: <strong>{selectedClient.razon_social || selectedClient.nombre}</strong>
                        </span>
                    }
                    description={
                        <span>
                            CUIT: {selectedClient.documento_nro || selectedClient.cuit || 'Sin CUIT'} | 
                            Saldo actual: <Text strong style={{ color: (selectedClient.saldo_pendiente || 0) > 0 ? '#cf1322' : '#52c41a' }}>
                                {formatCurrency(selectedClient.saldo_pendiente || 0)}
                            </Text>
                        </span>
                    }
                    type="info"
                    showIcon
                    style={{ marginBottom: 16 }}
                />
            )}

            <Form
                form={form}
                layout="vertical"
                autoComplete="off"
            >
                <Divider orientation="left">Tipo de Movimiento</Divider>

                <Form.Item
                    name="tipoMovimiento"
                    label="Seleccione el tipo de movimiento"
                    rules={[{ required: true, message: 'Por favor seleccione el tipo de movimiento' }]}
                >
                    <Select
                        placeholder="Seleccionar tipo..."
                        size="large"
                        onChange={handleTypeChange}
                    >
                        {Object.entries(MOVEMENT_TYPES).map(([key, config]) => (
                            <Option key={key} value={key}>
                                <Space>
                                    <span style={{ color: config.color }}>{config.icon}</span>
                                    <span>{config.label}</span>
                                    <Text type="secondary" style={{ fontSize: 12 }}>
                                        ({config.column})
                                    </Text>
                                </Space>
                            </Option>
                        ))}
                    </Select>
                </Form.Item>

                {/* Movement type description */}
                {selectedType && (
                    <Alert
                        message={MOVEMENT_TYPES[selectedType].label}
                        description={MOVEMENT_TYPES[selectedType].description}
                        type={MOVEMENT_TYPES[selectedType].column === 'HABER' ? 'success' : 'warning'}
                        showIcon
                        icon={MOVEMENT_TYPES[selectedType].icon}
                        style={{ marginBottom: 16 }}
                    />
                )}

                <Divider orientation="left">Datos del Movimiento</Divider>

                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item
                            name="monto"
                            label="Monto"
                            rules={[
                                { required: true, message: 'Por favor ingrese el monto' },
                                { 
                                    validator: (_, value) => {
                                        const numValue = parseFloat(value);
                                        if (isNaN(numValue) || numValue < 0.01) {
                                            return Promise.reject('El monto debe ser mayor a cero');
                                        }
                                        return Promise.resolve();
                                    }
                                }
                            ]}
                        >
                            <InputNumber
                                style={{ width: '100%' }}
                                size="large"
                                prefix="$"
                                precision={2}
                                decimalSeparator=","
                                placeholder="0,00"
                                min={0}
                                max={99999999.99}
                                controls={false}
                                stringMode
                                parser={(value) => {
                                    if (!value) return '';
                                    // Remove $ prefix and spaces
                                    let cleaned = String(value).replace(/\$/g, '').trim();
                                    // Replace comma with dot for decimal (accept both separators)
                                    cleaned = cleaned.replace(',', '.');
                                    // Remove any non-numeric characters except dot
                                    cleaned = cleaned.replace(/[^\d.]/g, '');
                                    return cleaned;
                                }}
                                formatter={(value) => {
                                    if (!value) return '';
                                    // Just return the value with comma as decimal separator
                                    return String(value).replace('.', ',');
                                }}
                            />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            name="fecha"
                            label="Fecha"
                            rules={[{ required: true, message: 'Por favor seleccione la fecha' }]}
                        >
                            <DatePicker
                                style={{ width: '100%' }}
                                size="large"
                                format="DD/MM/YYYY"
                                placeholder="Seleccionar fecha"
                            />
                        </Form.Item>
                    </Col>
                </Row>

                {/* Payment-specific fields */}
                {isPaymentType && (
                    <>
                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item
                                    name="paymentMethod"
                                    label="Método de Pago"
                                    rules={[{ required: isPaymentType, message: 'Seleccione método de pago' }]}
                                >
                                    <Select placeholder="Seleccionar método..." size="large">
                                        {PAYMENT_METHODS.map((method) => (
                                            <Option key={method.value} value={method.value}>
                                                <Space>
                                                    {method.icon}
                                                    <span>{method.label}</span>
                                                    {method.triggersCaja && (
                                                        <Text type="secondary" style={{ fontSize: 11 }}>
                                                            ✓ Caja
                                                        </Text>
                                                    )}
                                                </Space>
                                            </Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item
                                    name="paymentReference"
                                    label="Nro. Comprobante / Referencia"
                                >
                                    <Input 
                                        size="large"
                                        placeholder="Ej: REC-001234, TRF-XYZ" 
                                    />
                                </Form.Item>
                            </Col>
                        </Row>

                        {/* Caja integration note */}
                        <Form.Item shouldUpdate noStyle>
                            {() => {
                                const pm = form.getFieldValue('paymentMethod');
                                const isCash = pm === '201';
                                return (
                                    <Alert
                                        type={isCash ? 'success' : 'warning'}
                                        showIcon
                                        style={{ marginBottom: 16 }}
                                        message={
                                            isCash
                                                ? '✓ Efectivo — este pago impactará en la Caja del día'
                                                : '⚠ Este método NO impacta en la Caja (no es efectivo)'
                                        }
                                    />
                                );
                            }}
                        </Form.Item>
                    </>
                )}

                <Form.Item
                    name="notas"
                    label="Notas / Descripción"
                    rules={[
                        { required: true, message: 'Por favor ingrese una nota o descripción' },
                        { min: 5, message: 'La nota debe tener al menos 5 caracteres' }
                    ]}
                >
                    <TextArea 
                        rows={2} 
                        placeholder="Descripción del movimiento (mínimo 5 caracteres)..." 
                    />
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default AgregarMovimientoModal;
